import type { ProblemDetails } from '@levitas/contracts';

const BASE = '/api/v1';

/** Erro vindo da API já traduzido para algo que dá para mostrar na tela. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Mensagem do campo, quando a API devolveu erro por campo. */
  campo(nome: string): string | undefined {
    return this.fieldErrors?.[nome]?.[0];
  }

  /**
   * A frase mais útil que a API mandou. Quando a recusa é de um campo, é a do
   * campo que ensina — "Confira os campos destacados" só serve quando existe
   * um formulário com campos destacados na tela.
   */
  get mensagemUtil(): string {
    const doCampo = Object.values(this.fieldErrors ?? {})[0]?.[0];
    return doCampo ?? this.message;
  }
}

let accessToken: string | null = null;
let aoPerderSessao: (() => void) | null = null;
let renovando: Promise<ResultadoDaRenovacao> | null = null;

export function definirToken(token: string | null): void {
  accessToken = token;
}

export function tokenAtual(): string | null {
  return accessToken;
}

export function aoExpirarSessao(callback: () => void): void {
  aoPerderSessao = callback;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Evita laço infinito quando a própria renovação falha. */
  semRenovar?: boolean;
}

export async function api<T>(caminho: string, options: RequestOptions = {}): Promise<T> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (resposta.status === 401 && !options.semRenovar) {
    const renovou = await renovarSessao();
    if (renovou === 'ok') return api<T>(caminho, { ...options, semRenovar: true });
    // Sem rede não é sessão perdida: derrubar quem está offline trocaria um
    // problema de sinal por um pedido de senha que ninguém tem como atender.
    if (renovou === 'recusada') aoPerderSessao?.();
  }

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  const dados: unknown = texto ? JSON.parse(texto) : null;

  if (!resposta.ok) {
    const problema = dados as ProblemDetails | null;
    throw new ApiError(
      resposta.status,
      problema?.code,
      problema?.detail ?? problema?.title ?? 'Não foi possível concluir. Tente de novo.',
      problema?.errors,
    );
  }

  return dados as T;
}

/**
 * Baixa um arquivo da API (M14).
 *
 * O `fetch` leva o token como qualquer outra chamada; o navegador salva o
 * arquivo com o nome que a API mandou no cabeçalho — é ele que a pessoa vai
 * encontrar depois na pasta de downloads.
 */
export async function baixarArquivo(caminho: string): Promise<void> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });

  if (!resposta.ok) {
    const texto = await resposta.text();
    const problema = texto ? (JSON.parse(texto) as ProblemDetails) : null;

    throw new ApiError(
      resposta.status,
      problema?.code,
      problema?.detail ?? problema?.title ?? 'Não foi possível gerar o arquivo.',
      problema?.errors,
    );
  }

  const conteudo = await resposta.blob();
  const endereco = URL.createObjectURL(conteudo);

  const link = document.createElement('a');
  link.href = endereco;
  link.download = nomeDoCabecalho(resposta.headers.get('content-disposition')) ?? 'levitas';
  document.body.append(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(endereco);
}

function nomeDoCabecalho(cabecalho: string | null): string | null {
  const achado = cabecalho?.match(/filename="?([^"]+)"?/);
  return achado?.[1] ?? null;
}

/**
 * O que aconteceu ao tentar renovar a sessão.
 *
 * `recusada` e `sem-rede` **não** são a mesma coisa, e tratá-las igual custaria
 * caro: a primeira significa que a sessão acabou e a pessoa precisa entrar de
 * novo; a segunda, que o aparelho está sem sinal — e aí mandar a pessoa para a
 * tela de login é o contrário do que ela precisa (seção 9.5).
 */
export type ResultadoDaRenovacao = 'ok' | 'recusada' | 'sem-rede';

/**
 * Uma renovação por vez: se três requisições esbarrarem no 401 juntas, elas
 * esperam a mesma promessa em vez de queimarem três refresh tokens.
 */
export function renovarSessao(tenantId?: string): Promise<ResultadoDaRenovacao> {
  renovando ??= (async (): Promise<ResultadoDaRenovacao> => {
    try {
      const resposta = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tenantId ? { tenantId } : {}),
      });
      if (!resposta.ok) return 'recusada';
      const dados = (await resposta.json()) as { accessToken: string };
      accessToken = dados.accessToken;
      return 'ok';
    } catch {
      return 'sem-rede';
    } finally {
      renovando = null;
    }
  })();

  return renovando;
}
